/** @jsxImportSource @emotion/react */
import { Banners, BasePage } from 'components';
import { useMerchantData } from 'contexts';
import { MerchantProfileStepStatus, PageRoute } from 'enums';
import { css, isProduction, isSandbox } from 'global';
import {
  CompleteMfaBannerModule,
  DashboardOverviewModule,
  DashboardTilesModule,
  EnableMFADialogModule,
  PromoCarousel,
} from 'modules';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageProps } from 'types';
import * as styles from './DashboardPage.styles';
import { getGreeting } from 'utils';

const DashboardPage: FC<PageProps> = () => {
  const { merchantProfile, shouldShowOverview, profileData, isAdmin } =
    useMerchantData();
  const navigate = useNavigate();

  const settlementDetailsCompleted =
    merchantProfile?.settlement === MerchantProfileStepStatus.Completed;
  const merchantProfileSubmitted = merchantProfile?.profile.submitted;
  const settlementDetailsImported =
    profileData?.merchantData?.hasSettlementDetails;

  // show SettlementDetails banner to Admin users if settlement details are missing
  const settlementDetailsRequired =
    merchantProfile &&
    !merchantProfileSubmitted &&
    !settlementDetailsCompleted &&
    (!isProduction || (isAdmin && !settlementDetailsImported));

  const showApplicationCompletedBanner =
    merchantProfile &&
    !isProduction &&
    (settlementDetailsCompleted || merchantProfileSubmitted);

  return (
    <>
      <CompleteMfaBannerModule />
      <BasePage title={null} variant="layout" fullWidth>
        {settlementDetailsRequired && (
          <Banners.Notice
            size={{ xs: 12 }}
            css={styles.merchantProfileGrid}
            title="Next, add settlement details"
            content={
              isProduction
                ? 'Submit settlement details to activate your merchant account.'
                : 'Submit settlement details to finalise your application.'
            }
            action={(): void => navigate(PageRoute.Application)}
            actionLabel="Add details"
          />
        )}
        {showApplicationCompletedBanner && (
          <Banners.Notice
            size={{ xs: 12 }}
            css={styles.merchantProfileGrid}
            title="Application completed"
            content={
              <p css={css.noBtmMrgn}>
                Application updates will come via email in the next 1-2 days.
                For any questions, call us on{' '}
                <a href="tel:0282942345">02 8294 2345</a>.
              </p>
            }
          />
        )}
        <p className="title3" style={{ marginTop: '16px' }}>
          {getGreeting()}
        </p>
        {!isSandbox && (
          <>
            <PromoCarousel />
          </>
        )}

        {(shouldShowOverview() && <DashboardOverviewModule />) || (
          <DashboardTilesModule />
        )}
      </BasePage>
      <EnableMFADialogModule />
    </>
  );
};

export default DashboardPage;
